/**
 * Classify-and-fit (plan §4.3), assembled from the registered families:
 * - `familiesPrompt()`: every family's classifier paragraph and parameter guide;
 * - `fitOutputSchema()`: the strict FitResult with each family's parameter block (Zod, so the
 *   same definition gives Claude its structured-output schema and validates the answer);
 * - `proposalFromFit()`: field provenance for Check (published facts quoted, image readings
 *   proposed, never confirmed) plus the fitted block for `designFromProposal()`.
 */
import { z } from 'zod';
import type { Design, Fields } from '../domain/schemas';
import type { ProposalField, ProposalWithFields } from '../domain/fields';
import { mm, toMm } from '../domain/units';
import { FAMILIES, FAMILY_IDS, familyLabel, getFamily } from './registry';
import { decideFit, FeaturesSchema, FitEvidenceOutputSchema, FitResultSchema, FLAG_AT, RecognisedFactSchema, StructureSchema, type FitResult } from './fit-schema';
import { STOCK } from '../domain/profiles';
import { isBoardStock, stockForThickness, STOCKS, type AnyStock } from './stock';
import type { GridFitParams } from './grid-bookcase/intake';
import type { Family } from './registry-types';
import { applyFix } from './fix';
import type { FrontSpec } from './fronts';

export * from './fit-schema';
/** The family ids a fit may name (the registry's). */
export const FAMILY_IDS_FOR_FIT: readonly string[] = FAMILY_IDS;

/**
 * Two-stage intake (plan §4.3, revised in Phase 4): the single classify-and-fit schema with every
 * family's block exceeded the structured-output grammar budget, so analysis is two calls.
 *
 * Stage (a), classify — a small schema: candidates, chosen, fitsAFamily, noFitReason, what was
 * seen and the shared proportions. No numbers about the layout.
 */
export const ClassifySharedSchema = z.object({
  widthOverHeight: z.number().min(0).describe('Overall width divided by overall height on the frontal photo, corrected for perspective; 0 if you cannot tell.'),
  depthOverWidth: z.number().min(0).describe('Depth divided by width (from a side or three-quarter view); 0 if you cannot tell.'),
  boardThicknessOverWidth: z.number().min(0).describe('Visible board (shelf) thickness divided by the overall width; 0 if you cannot tell.'),
  material: z.string().describe('Material in plain words, or empty.'),
  colour: z.string().describe('Colour in plain words, or empty.'),
  finish: z.string().describe('Finish in plain words, or empty.'),
});

export function classifyOutputSchema() {
  const ids = FAMILY_IDS as [string, ...string[]];
  return z.object({
    candidates: z.array(z.object({ familyId: z.enum([...ids, 'none'] as [string, ...string[]]), confidence: z.number().min(0).max(1), why: z.string() })).min(1)
      .describe('Every family that could plausibly describe the piece, most likely first, with your confidence 0–1 and one short reason the customer will read.'),
    chosen: z.enum([...ids, 'none'] as [string, ...string[]]).describe('The most likely family, or "none" when none fits.'),
    fitsAFamily: z.boolean(),
    noFitReason: z.string().describe('When nothing fits: one short British-English sentence the customer will read, saying only what is plainly visible, with no numbers or counts. Empty when a family fits.'),
    seen: z.object({ description: z.string(), bays: z.int().min(0) }).describe('What the photo shows in plain words, and how many bays side by side (0 if it is not shelving or has no bays).'),
    shared: ClassifySharedSchema,
    structure: StructureSchema.describe('The structure you see, board by board (see "structure" above).'),
    features: FeaturesSchema.describe('Doors, drawers, slatted sides, legs (see "features" above).'),
  });
}

/** Parsing a classification: structure and features may be absent (answers recorded before them); candidates are filtered to registered families first. */
export const classifyParseSchema = () => classifyOutputSchema().extend({
  candidates: z.array(z.object({ familyId: z.string(), confidence: z.number().min(0).max(1), why: z.string() })),
  structure: StructureSchema.optional(), features: FeaturesSchema.optional(),
});

/** The confidence given to a chosen family when the classification carried no usable candidates. */
export const DEGENERATE_CONFIDENCE = 0.5;

/**
 * A classification that chose a registered family and says it fits, but whose candidates are
 * empty or degenerate (no registered family, or every confidence 0) — seen once in pilot-v1-r2
 * (p08: `candidates: [{familyId: "", confidence: 0, why: ""}], chosen: "cube-storage"`).
 */
export function isDegenerate(raw: { candidates?: { familyId?: string; confidence?: number }[]; chosen?: string; fitsAFamily?: boolean }): boolean {
  if (!raw.fitsAFamily || !raw.chosen || !FAMILY_IDS.includes(raw.chosen)) return false;
  const usable = (raw.candidates ?? []).filter((c) => FAMILY_IDS.includes(c?.familyId ?? '') && (c.confidence ?? 0) > 0);
  return usable.length === 0;
}

/**
 * Proceed with a degenerate classification's chosen family, never a refusal: the chosen family at
 * 0.50 (the flagged "not sure" band, so Check shows the runners-up), the registered candidates kept
 * as runners-up.
 */
export function repairDegenerate<T extends { candidates?: { familyId?: string; confidence?: number; why?: string }[]; chosen: string }>(raw: T): T {
  const others = (raw.candidates ?? []).filter((c) => FAMILY_IDS.includes(c?.familyId ?? '') && c.familyId !== raw.chosen);
  const why = 'The analysis did not give a confidence for this style, so the match is marked as unsure; check it against your photo.';
  return { ...raw, candidates: [{ familyId: raw.chosen, confidence: DEGENERATE_CONFIDENCE, why }, ...others] };
}
type ClassifyOut = z.infer<ReturnType<typeof classifyOutputSchema>>;
/** A classification (structure and features are absent in answers recorded before the pilot fixes). */
export type ClassifyResult = Omit<ClassifyOut, 'structure' | 'features'> & Partial<Pick<ClassifyOut, 'structure' | 'features'>>;

/** Stage (b), fit — only the chosen family's parameter block, plus published facts and evidence. */
export function familyFitOutputSchema(familyId: string) {
  const fam = getFamily(familyId);
  if (!fam) throw new Error(`Unknown family ${familyId}`);
  return z.object({
    params: (fam.intake.paramsSchema as z.ZodType).describe(`The ${familyId} parameter block, read from the photo.`),
    recognisedFacts: z.array(RecognisedFactSchema),
    evidence: FitEvidenceOutputSchema,
    evidenceNotes: z.array(z.string()),
    notCaptured: z.array(z.string()).describe('Visible structure the parameter block above cannot express, each in a few plain words ("a middle partition in every tier", "sides rise above the top"); [] when the block captures everything you see.'),
  });
}
export type FamilyFitResult = { params: unknown; recognisedFacts: FitResult['recognisedFacts']; evidence: FitResult['evidence']; evidenceNotes: string[]; notCaptured?: string[] };

/** The stored FitResult from the two stages (no fit stage on a no-fit). */
export function assembleFit(c: ClassifyResult, f: FamilyFitResult | null): FitResult {
  const fitted = f && c.chosen !== 'none';
  return {
    candidates: c.candidates,
    chosen: c.chosen,
    fitsAFamily: c.fitsAFamily,
    noFitReason: c.noFitReason,
    seen: c.seen,
    params: fitted ? { [c.chosen]: f.params } : {},
    shared: c.shared,
    recognisedFacts: f?.recognisedFacts ?? [],
    evidence: f?.evidence ?? { carcass: { left: 0, right: 1, top: 0, bottom: 1 }, imageIndex: 1, textSpans: [] },
    evidenceNotes: f?.evidenceNotes ?? [],
    ...(c.structure ? { structure: c.structure } : {}),
    ...(c.features ? { features: c.features } : {}),
    ...(f?.notCaptured ? { notCaptured: f.notCaptured } : {}),
  };
}

/**
 * FND-06: a classification whose own structure reading contradicts its choice — inner verticals
 * that do not line up from row to row, in an irregular layout, but a grid or cube chosen — is
 * fitted as an offset grid with free dividers instead (when the offset grid is a candidate at
 * all). Its confidence never rises above the original choice's. Returns the classification to fit.
 */
export function preferFamily(c: ClassifyResult): ClassifyResult {
  const st = c.structure;
  if (!st || !['grid-bookcase', 'cube-storage'].includes(c.chosen)) return c;
  if (st.verticalsAlign !== 'no' || st.layout !== 'irregular' || st.fullHeightSides < 2) return c;
  const off = c.candidates.find((x) => x.familyId === 'offset-grid');
  const chosen = c.candidates.find((x) => x.familyId === c.chosen);
  if (!off || !chosen) return c;
  const conf = Math.min(chosen.confidence, Math.max(off.confidence, FLAG_AT));
  const candidates = [{ ...off, confidence: conf, why: off.why }, ...c.candidates.filter((x) => x.familyId !== 'offset-grid')];
  return { ...c, chosen: 'offset-grid', candidates };
}

/** Whether a classification goes on to the fit stage (the same rule decideFit applies afterwards). */
export function shouldFit(c: ClassifyResult): string | null {
  if (!c.fitsAFamily || c.chosen === 'none' || !FAMILY_IDS.includes(c.chosen)) return null;
  const conf = c.candidates.find((x) => x.familyId === c.chosen)?.confidence ?? 0;
  return conf >= FLAG_AT ? c.chosen : null;
}

/** The families section of the classify prompt: every family's classifier paragraph. */
export function familiesPrompt(): string {
  return FAMILIES.map((f) => f.intake.classifier).join('\n\n');
}

/** The fit-stage instruction for one family: its paragraph again and its parameter guide. */
export function familyFitPrompt(familyId: string): string {
  const fam = getFamily(familyId);
  if (!fam) throw new Error(`Unknown family ${familyId}`);
  return `${fam.intake.classifier}\n\n${fam.intake.paramsGuide}`;
}

/** Older recordings: the grid's `plinth: 'yes' | 'no'` (before `base`) is read as the base. */
function upgradeBlock(familyId: string, raw: unknown): unknown {
  if (familyId !== 'grid-bookcase' || !raw || typeof raw !== 'object') return raw;
  const b = raw as Record<string, unknown>;
  if (b.base !== undefined || b.plinth === undefined) return raw;
  return { ...b, base: b.plinth === 'yes' ? 'plinth' : b.plinth === 'no' ? 'floor' : 'unknown', baseHeightOverHeight: typeof b.plinthOverHeight === 'number' ? b.plinthOverHeight : 0 };
}

/** Parse and check a stored (assembled) FitResult. Returns it or a reason it is malformed. */
export function parseFit(json: unknown): { ok: true; fit: FitResult } | { ok: false; reason: string } {
  const r = FitResultSchema.safeParse(json);
  if (!r.success) {
    const i = r.error.issues[0];
    return { ok: false, reason: `schema: ${i ? `${i.path.join('.')}: ${i.message}` : 'invalid'}` };
  }
  const fit = r.data;
  if (fit.chosen !== 'none' && !FAMILY_IDS.includes(fit.chosen)) return { ok: false, reason: `schema: chosen: ${fit.chosen} is not a family` };
  // a front quadrilateral that is not four points inside the photo is dropped, never fatal
  const q = fit.evidence.frontQuad;
  if (q && (q.length !== 4 || q.some((pt) => !(pt.x >= -0.05 && pt.x <= 1.05 && pt.y >= -0.05 && pt.y <= 1.05)))) delete fit.evidence.frontQuad;
  const d = decideFit(fit, FAMILY_IDS);
  if (d.kind === 'proposal') {
    const fam = getFamily(d.familyId)!;
    const block = (fam.intake.paramsSchema as z.ZodType).safeParse(upgradeBlock(d.familyId, fit.params[d.familyId]));
    if (!block.success) {
      const i = block.error.issues[0];
      return { ok: false, reason: `schema: params.${d.familyId}.${i ? `${i.path.join('.')}: ${i.message}` : 'invalid'}` };
    }
    const bad = fam.checkParams(block.data as never);
    if (bad) return { ok: false, reason: bad };
    fit.params = { ...fit.params, [d.familyId]: block.data };
  }
  return { ok: true, fit };
}

// ---------------------------------------------------------------------------------------------
// Field provenance
// ---------------------------------------------------------------------------------------------

const UNIT_TO_MM: Record<string, number> = { mm: 1, millimetre: 1, millimetres: 1, cm: 10, centimetre: 10, centimetres: 10, m: 1000, metre: 1000, metres: 1000, in: 25.4, inch: 25.4, inches: 25.4, '"': 25.4 };
const DIMENSIONS = ['W', 'H', 'D', 'stockThickness'] as const;
const fmt = (v: number) => (Number.isInteger(v) ? String(v) : v.toFixed(1));
const toMmValue = (value: number, unit: string | null): number | null => {
  const k = unit ? UNIT_TO_MM[unit.trim().toLowerCase()] : undefined;
  return k === undefined ? null : Math.round(value * k * 10) / 10;
};

/**
 * Field provenance from a FitResult (the chosen family's block). Text facts become
 * `published` with the quote, span and conversion; image readings `proposed`.
 */
export function proposalFromFit(fit: FitResult, familyId: string, { imageId, createdAt }: { imageId?: string; createdAt: string }): ProposalWithFields {
  const fields: Record<string, ProposalField> = {};
  const span = (kind: string) => fit.evidence.textSpans.find((s) => s.kind === kind);
  const imgEv = (note: string) => ({ ...(imageId ? { imageId } : {}), note });
  const fact = (kind: string, source: 'text' | 'image') => fit.recognisedFacts.find((f) => f.kind === kind && f.source === source);
  const fam = getFamily(familyId);
  const params = fit.params[familyId] ?? null;
  const cand = fit.candidates.find((c) => c.familyId === familyId);

  fields.family = {
    value: familyId,
    display: `${familyLabel(familyId)}${fam && params ? ` · ${fam.fitSummary(params as never)}` : ''}`,
    status: 'proposed', source: 'image',
    evidence: imgEv(cand?.why || fit.seen.description),
  };

  for (const kind of DIMENSIONS) {
    const t = fact(kind, 'text');
    const i = fact(kind, 'image');
    const f = t ?? i;
    if (!f || typeof f.value !== 'number') continue;
    const v = toMmValue(f.value, f.unit);
    if (v === null) continue;
    const conversion = f.unit && f.unit.trim().toLowerCase() !== 'mm' ? `${f.value} ${f.unit} = ${fmt(v)} mm` : undefined;
    if (t) {
      const s = span(kind);
      if (!s) continue;
      fields[kind] = { value: v, unit: 'mm', display: `${fmt(v)} mm`, status: 'published', source: 'text', evidence: { quote: s.text, span: s.span, ...(conversion ? { note: conversion } : {}) } };
    } else {
      fields[kind] = { value: v, unit: 'mm', display: `${fmt(v)} mm`, status: 'proposed', source: 'image', evidence: imgEv(`Estimated from the photo${conversion ? ` (${conversion})` : ''}.`) };
    }
  }

  // Grid-family readings (the only family registered today; later families add theirs).
  const grid = familyId === 'grid-bookcase' ? (params as GridFitParams | null) : null;
  if (grid) {
    fields.bays = { value: grid.bays, display: `${grid.bays} bay${grid.bays === 1 ? '' : 's'}`, status: 'proposed', source: 'image', evidence: imgEv(`${grid.bays} bay${grid.bays === 1 ? '' : 's'} counted between the full-height uprights.`) };
    const first = grid.bayLayouts[0];
    const shelfText = fact('shelfCount', 'text');
    if (first) {
      const counts = grid.bayLayouts.map((l) => l.shelfCount);
      const same = counts.every((c) => c === counts[0]);
      const n = shelfText && typeof shelfText.value === 'number' && grid.bays === 1 ? shelfText.value : first.shelfCount;
      const display = grid.bays === 1 || !same ? (same ? `${n} shel${n === 1 ? 'f' : 'ves'}` : `${counts.join(' / ')} shelves`) : `${n} shel${n === 1 ? 'f' : 'ves'} per bay`;
      if (shelfText && typeof shelfText.value === 'number' && grid.bays === 1 && span('shelfCount')) {
        const s = span('shelfCount')!;
        const note = first.shelfCount !== shelfText.value ? `The photo shows ${first.shelfCount} shelves.` : undefined;
        fields.shelfCount = { value: n, display, status: 'published', source: 'text', evidence: { quote: s.text, span: s.span, ...(imageId ? { imageId } : {}), ...(note ? { note } : {}) } };
      } else {
        fields.shelfCount = { value: n, display, status: 'proposed', source: 'image', evidence: imgEv(grid.bays === 1 ? `${n} internal shelves counted in the photo.` : `Shelves counted bay by bay: ${counts.join(', ')}.`) };
      }
      const spacing = grid.bayLayouts.map((l) => l.spacing);
      if (spacing.every((e) => e !== 'unknown')) {
        const even = spacing.every((e) => e === 'even');
        fields.even = { value: even, display: even ? 'Even' : 'Uneven', status: 'proposed', source: 'image', evidence: imgEv(even ? 'The openings look equal.' : 'The openings differ in height.') };
      }
      if (first.openingRatios.length) {
        const ratios = first.openingRatios;
        fields.openingRatios = {
          value: ratios, display: ratios.map((r) => (r * 100).toFixed(1) + '%').join(' / '), status: 'proposed', source: 'image',
          evidence: imgEv(fit.evidenceNotes[0] ?? 'Measured from the shelf lines in the photo, bottom-up.'),
        };
      }
    }
    if (grid.back !== 'unknown') fields.back = { value: grid.back, display: grid.back === 'overlay' ? 'Overlay back' : grid.back === 'rebated' ? 'Rebated back' : 'No back', status: 'proposed', source: 'image', evidence: imgEv('Seen in the photo.') };
    const base = grid.base ?? (grid.plinth === 'yes' ? 'plinth' : grid.plinth === 'no' ? 'floor' : 'unknown');
    if (base !== 'unknown') {
      const on = base === 'plinth';
      fields.plinth = {
        value: on, display: on ? 'Plinth' : base === 'floor' ? 'No plinth' : base === 'sides' ? 'Sides to the floor' : 'Feet', status: 'proposed', source: 'image',
        evidence: imgEv(on ? 'The bottom sits on a kick board.' : base === 'floor' ? 'The bottom is at the floor.' : base === 'sides' ? 'The sides run down past the bottom to the floor; the bottom is raised with an open gap under it.' : `The piece stands on ${base === 'legs' ? 'legs' : 'feet'}; shown on ${base === 'legs' ? 'the kit\'s feet' : 'feet'}.`),
      };
    }
  }

  for (const kind of ['material', 'finish'] as const) {
    const t = fact(kind, 'text');
    if (!t) continue;
    const s = span(kind);
    if (!s) continue;
    const value = String(t.value);
    fields[kind] = { value, display: value, status: 'published', source: 'text', evidence: { quote: s.text, span: s.span } };
  }

  return { createdAt, fields, fit: { familyId, params, shared: fit.shared } };
}

/**
 * Starting size for a proposal: published (or image) W, H, D in mm, else a starting value
 * from the fitted proportions — never presented as a measurement: those keys are returned
 * as `unresolved` so Check asks for them.
 */
type Range = { min: number; max: number };
const GRID_RANGES = { W: { min: 400, max: 2400 }, H: { min: 600, max: 2200 }, D: { min: 200, max: 450 } };

/** A family's W, H, D ranges in mm (from its definition; the grid's when absent). */
export function sizeRanges(familyId: string | null | undefined): { W: Range; H: Range; D: Range } {
  const def = getFamily(familyId)?.definition;
  const r = (k: 'W' | 'H' | 'D') => {
    const p = def?.parameters.find((x) => x.key === k)?.range;
    return p ? { min: p.min / 10, max: p.max / 10 } : GRID_RANGES[k];
  };
  return { W: r('W'), H: r('H'), D: r('D') };
}

export function startingSize(pwf: ProposalWithFields, fallback: { W: number; H: number; D: number }, ranges = sizeRanges(pwf.fit?.familyId)): { W: number; H: number; D: number; unresolved: string[] } {
  const f = pwf.fields;
  const shared = pwf.fit?.shared;
  const unresolved: string[] = [];
  const num = (k: string) => (f[k] && Number.isFinite(Number(f[k]!.value)) ? Number(f[k]!.value) : null);
  let W = num('W'), H = num('H'), D = num('D');
  const woh = shared?.widthOverHeight && shared.widthOverHeight > 0 ? shared.widthOverHeight : null;
  const dow = shared?.depthOverWidth && shared.depthOverWidth > 0 ? shared.depthOverWidth : null;
  const r10 = (v: number) => Math.round(v / 10) * 10;
  const clamp = (v: number, k: 'W' | 'H' | 'D') => r10(Math.min(ranges[k].max, Math.max(ranges[k].min, v)));
  if (H === null && W === null && woh) {
    // FND-04: neither size is published, so both come from the photo's proportions. Start from the
    // fallback height; when the width that gives is out of range, take the nearest width in range
    // and derive the height from it, so the starting size keeps the photo's width:height.
    unresolved.push('H', 'W');
    let h = fallback.H;
    let w = h * woh;
    if (w > ranges.W.max) { w = ranges.W.max; h = w / woh; }
    if (w < ranges.W.min) { w = ranges.W.min; h = w / woh; }
    H = clamp(h, 'H');
    W = clamp(H * woh, 'W');
  }
  if (H === null) { unresolved.push('H'); H = W !== null && woh ? clamp(W / woh, 'H') : clamp(fallback.H, 'H'); }
  if (W === null) { unresolved.push('W'); W = woh ? clamp(H * woh, 'W') : clamp(fallback.W, 'W'); }
  if (D === null) { unresolved.push('D'); D = dow ? clamp(W * dow, 'D') : clamp(fallback.D, 'D'); }
  return { W, H, D, unresolved };
}

/** A design from a proposal (the fitted family's block, or the first slice's fields). */
export function designFromProposal(pwf: ProposalWithFields, stock: AnyStock = STOCK, fallback: { W: number; H: number; D: number } = { W: 800, H: 1200, D: 300 }): { design: Design; unresolved: string[] } {
  const size = startingSize(pwf, fallback);
  const fam = getFamily(pwf.fit?.familyId ?? 'grid-bookcase') ?? FAMILIES[0]!;
  // FND-23: a chunky solid frame (boards read 32 mm or more thick) is built from thick solid stock
  const thick = thickStockFor(pwf, size.W, stock);
  if (thick) stock = thick;
  const f = pwf.fields;
  const ratios = Array.isArray(f.openingRatios?.value) ? (f.openingRatios!.value as number[]) : null;
  const design = fam.designFromFit({
    W: mm(size.W), H: mm(size.H), D: mm(size.D),
    params: (pwf.fit?.params as never) ?? null,
    fallback: { n: Number(f.shelfCount?.value ?? 3), even: !!f.even?.value, openingRatios: ratios },
    stock,
  });
  return { design: withFittedFronts(design, fam, (pwf.fit?.params as { fronts?: FittedFront[] } | undefined)?.fronts), unresolved: size.unresolved };
}

type FittedFront = { box: number[]; kind: FrontSpec['kind']; mount: 'overlay' | 'inset'; hinge: 'left' | 'right' | 'pair' | 'none'; drawers: number };

/**
 * The fit's doors and drawers (boxes over the front, fractions of W and H from the floor) as fronts on the
 * built design: each box covers the openings whose centres fall inside it (or the one it overlaps most).
 * A stack of drawers over several openings becomes a stack in each; door spans keep together. Any fronts
 * error the rules offer a fix for (no board to hang from, too shallow for runners) is fixed here, as the
 * families' post-fits do.
 */
export function withFittedFronts(design: Design, fam: Family, fronts: readonly FittedFront[] | undefined): Design {
  if (!fronts?.length) return design;
  let built;
  try { built = fam.generate(design); } catch { return design; }
  const W = toMm(design.W), H = toMm(design.H);
  const specs: FrontSpec[] = [];
  const taken = new Set<string>();
  for (const f of fronts) {
    if (!Array.isArray(f.box) || f.box.length !== 4 || !f.box.every((v) => Number.isFinite(v))) continue;
    const [xa, xb, ya, yb] = f.box.map((v) => Math.max(0, Math.min(1, v))) as [number, number, number, number];
    const r = { x0: Math.min(xa, xb) * W, x1: Math.max(xa, xb) * W, y0: Math.min(ya, yb) * H, y1: Math.max(ya, yb) * H };
    const free = built.openings.filter((o) => !taken.has(o.id));
    let ids = free.filter((o) => { const cx = (o.bounds.x0 + o.bounds.x1) / 2, cy = (o.bounds.y0 + o.bounds.y1) / 2; return cx >= r.x0 && cx <= r.x1 && cy >= r.y0 && cy <= r.y1; }).map((o) => o.id);
    if (!ids.length) {
      const best = free.map((o) => ({ id: o.id, a: Math.max(0, Math.min(o.bounds.x1, r.x1) - Math.max(o.bounds.x0, r.x0)) * Math.max(0, Math.min(o.bounds.y1, r.y1) - Math.max(o.bounds.y0, r.y0)) })).sort((a, b) => b.a - a.a)[0];
      if (best && best.a > 0) ids = [best.id];
    }
    if (!ids.length) continue;
    ids.forEach((id) => taken.add(id));
    const base = { kind: f.kind, mount: f.mount, pull: 'knob' as const };
    if (f.kind === 'drawers') {
      const each = Math.max(1, Math.min(4, Math.round(Math.max(1, f.drawers) / ids.length)));
      for (const id of ids) specs.push({ ...base, openings: [id], drawers: each });
    } else specs.push({ ...base, openings: ids, ...(f.kind === 'sliding' ? {} : { hinge: f.hinge === 'none' ? 'left' : f.hinge }) });
  }
  if (!specs.length) return design;
  let d: Design = design.params ? (fam.setParam?.(design, 'fronts', specs) ?? { ...design, params: { ...design.params, fronts: specs } }) : { ...design, fronts: specs as unknown as Record<string, unknown>[] };
  for (let i = 0; i < 8; i++) {
    const fix = fam.generate(d).validation.errors.find((e) => e.field === 'fronts' && e.fix)?.fix;
    if (!fix) break;
    d = applyFix(d, fix);
  }
  return d;
}

/**
 * The thick solid stock for a fit whose boards read 32 mm or more (board thickness over width ×
 * the width), or null to keep the given stock. Ply and a stated thin board stay as they are; the
 * species follows the material words (oak, else sheesham). A published thickness wins.
 */
export function thickStockFor(pwf: ProposalWithFields, Wmm: number, stock: AnyStock): AnyStock | null {
  const shared = pwf.fit?.shared as Partial<FitResult['shared']> | undefined;
  const published = pwf.fields.stockThickness ? Number(pwf.fields.stockThickness.value) : null;
  const ratio = shared?.boardThicknessOverWidth ?? 0;
  const tMm = published && Number.isFinite(published) ? published : ratio > 0 ? ratio * Wmm : 0;
  const words = `${shared?.material ?? ''} ${shared?.finish ?? ''}`;
  if (/ply|mdf|chipboard|laminate|engineered|melamine|particle/i.test(words)) return null;
  const id = stockForThickness(tMm, words);
  if (!id) return null;
  const current = isBoardStock(stock) ? stock.thicknessTicks : 0;
  const next = STOCKS[id];
  return next && isBoardStock(next) && next.thicknessTicks > current ? next : null;
}

/** The provenance key for a parameter of the design, as shown in Check (W, H, D in mm). */
export const sizeOf = (d: Pick<Design, 'W' | 'H' | 'D'>): { W: number; H: number; D: number } => ({ W: toMm(d.W), H: toMm(d.H), D: toMm(d.D) });

export type { Fields };
